import React from "react";
import { Download, Copy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getGoogleMapsApiKey } from "@/services/googleMapsService";

interface RevealActionButtonsProps {
  targetUrl: string;
  currentViewState?: {
    lat: number;
    lng: number;
    heading: number;
    pitch: number;
    panorama?: google.maps.StreetViewPanorama;
  };
  coordinates?: {
    lat: number;
    lng: number;
  };
}

const RevealActionButtons: React.FC<RevealActionButtonsProps> = ({
  targetUrl,
  currentViewState,
  coordinates
}) => {
  const { toast } = useToast();

  const handleDownload = async () => {
    try {
      console.log('🔄 RevealActionButtons: Starting download with state:', currentViewState);
      
      // If we have access to the panorama instance, use it directly for the most accurate capture
      if (currentViewState?.panorama) {
        const panorama = currentViewState.panorama;
        const currentPov = panorama.getPov();
        const position = panorama.getPosition();
        
        if (position && currentPov) {
          // Get API key from edge function
          const apiKey = await getGoogleMapsApiKey();
          if (!apiKey) {
            toast({
              title: "Download failed",
              description: "Google Maps API key not configured.",
              variant: "destructive",
            });
            return;
          }

          const size = '1024x768';
          const fov = 90;
          
          // Use the exact current panorama state
          const imageUrl = `https://maps.googleapis.com/maps/api/streetview?size=${size}&location=${position.lat()},${position.lng()}&heading=${currentPov.heading}&pitch=${currentPov.pitch}&fov=${fov}&key=${apiKey}`;
          
          console.log('✅ RevealActionButtons: Using panorama instance for exact view:', {
            lat: position.lat(),
            lng: position.lng(),
            heading: currentPov.heading,
            pitch: currentPov.pitch
          });

          // Fetch the image and create a blob for download
          const response = await fetch(imageUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Create download link
          const link = document.createElement('a');
          link.href = url;
          link.download = `street-view-${Date.now()}.jpg`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(url);

          toast({
            title: "Download started",
            description: "The Street View image download has started.",
          });
          
          console.log('✅ RevealActionButtons: Download initiated successfully');
          return;
        }
      }
      
      // Fallback to static coordinates if panorama not available
      if (coordinates) {
        const apiKey = await getGoogleMapsApiKey();
        if (!apiKey) {
          toast({
            title: "Download failed",
            description: "Google Maps API key not configured.",
            variant: "destructive",
          });
          return;
        }

        const imageUrl = `https://maps.googleapis.com/maps/api/streetview?size=800x600&location=${coordinates.lat},${coordinates.lng}&heading=0&pitch=0&fov=90&key=${apiKey}`;
        console.log('✅ RevealActionButtons: Using coordinates fallback for download');

        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `street-view-${Date.now()}.jpg`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        window.URL.revokeObjectURL(url);

        toast({
          title: "Download started",
          description: "The Street View image download has started.",
        });
        return;
      }

      toast({
        title: "Download not available",
        description: "Unable to download this target type.",
        variant: "destructive",
      });
      
    } catch (error) {
      console.error('❌ RevealActionButtons: Download failed:', error);
      toast({
        title: "Download failed",
        description: "Failed to download the image. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      toast({
        title: "URL copied",
        description: "The target URL has been copied to your clipboard.",
      });
    } catch (error) {
      console.error('❌ RevealActionButtons: Copy failed:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy URL to clipboard. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleOpenExternal = () => {
    window.open(targetUrl, '_blank');
  };

  return (
    <TooltipProvider>
      <div className="flex gap-2 mt-4 justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleDownload}
              aria-label="Download current view"
            >
              <Download className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Download current view</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleCopyUrl}
              aria-label="Copy target URL to clipboard"
            >
              <Copy className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Copy target URL to clipboard</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full flex justify-center items-center hover:bg-accent transition-colors"
              onClick={handleOpenExternal}
              aria-label="Open in Google Maps"
            >
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Open in Google Maps</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default RevealActionButtons;